import './styles/custom-properties.css'
import './styles/preloader.css'
import 'react-datepicker/dist/react-datepicker.css'
import ReactDOM from 'react-dom/client'
import { createHashRouter, RouterProvider } from 'react-router-dom'
import { Provider } from 'react-redux'
import * as Sentry from '@sentry/react'
import { FloatingTree } from '@floating-ui/react'
import { StrictMode } from 'react'

import { TanstackQueryProvider } from '@/components/providers/tanstack-query-provider'
import { StyledComponentsThemeProvider } from '@/components/providers/styled-components-theme-provider'
import { PostHogProvider } from '@/components/providers/post-hog-provider'
import { SentryIdentify } from '@/components/providers/sentry-identify'
import { UserLocaleSync } from '@/components/providers/user-locale-sync'
import { I18nProvider } from '@/components/providers/i18n-provider'
import { routeConfig } from '@/router'
import { endImpersonation, isImpersonating, reloadAt, setImpersonationNotice } from '@/utils/auth'
import { canReloadNow, markReloaded } from '@/utils/build-version'

import '@/i18n'

import Root from './components/Root'
import configureStore from './store/configureStore'
import API from './api/api'
import { getCookie, removeAuthCookie } from './helpers/helpers'
import initSentry from './components/sentryConfig'

const redirectPathBasedDeepLink = () => {
  const { pathname, hash, search } = window.location

  if (hash) {
    return
  }

  if (pathname.startsWith('/change-password') || pathname.startsWith('/activate')) {
    window.location.replace(`/#${pathname}${search}`)
    return
  }
}

// The need for this redirect is described in a comment:
// https://elevateos.atlassian.net/jira/software/c/projects/EL/boards/64?assignee=712020%3Ad3de30c3-1a22-4a9d-b338-12a17404ff27&assignee=712020%3A6c7e5420-6074-419e-a17e-9bdc16849bde&selectedIssue=EL-4895
redirectPathBasedDeepLink()

initSentry()
// A deploy can delete the hashed theme CSS a stale `index.html` still points at; Vite reports the
// failed preload here, before React (and the other stale-build reload hooks) mounts. Fires for any
// failed dynamic-import preload, not just the theme — today the theme is the only one.
// Deliberately one attempt, no retry like useForcedReload: a cooldown-blocked event almost always
// means this reload already happened and didn't help, and Tier 0/1 pick the tab up once React
// mounts (with the dirty-form/popup guards). See docs/12-stale-detection.md.
window.addEventListener('vite:preloadError', () => {
  if (!canReloadNow()) return
  markReloaded()
  window.location.reload()
})

const sentryCreateHashRouter = Sentry.wrapCreateBrowserRouterV6(createHashRouter)
const router = sentryCreateHashRouter(routeConfig)

const loadTheme = async () => {
  const theme = import.meta.env.VITE_THEME

  try {
    if (import.meta.env.MODE === 'development') {
      await import(`./styles/styles-${theme}.scss`)
    } else {
      if (theme === 'chitowntrainer') {
        await import('./styles/styles-chitowntrainer.scss')
      } else if (theme === 'elevatedliving') {
        await import('./styles/styles-elevatedliving.scss')
      }
    }
  } catch (error) {
    console.error(`Failed to load theme styles for: ${theme}`, error)
  }
}

const renderApp = (store) => {
  const root = ReactDOM.createRoot(document.getElementById('app'))
  root.render(
    <StrictMode>
      <TanstackQueryProvider>
        <StyledComponentsThemeProvider>
          <I18nProvider>
            <Provider store={store}>
              <SentryIdentify>
                <UserLocaleSync>
                  <PostHogProvider>
                    <Root store={store}>
                      <FloatingTree>
                        <RouterProvider router={router} />
                      </FloatingTree>
                    </Root>
                  </PostHogProvider>
                </UserLocaleSync>
              </SentryIdentify>
            </Provider>
          </I18nProvider>
        </StyledComponentsThemeProvider>
      </TanstackQueryProvider>
    </StrictMode>,
  )
}

const bootstrapApp = async () => {
  const themePromise = loadTheme()

  let preloadedState = undefined

  if (getCookie('token') || getCookie('role')) {
    try {
      const data = await API.getUserData()
      preloadedState = { userData: { ...data, isAuth: true } }
    } catch (error) {
      // An impersonated session that no longer verifies falls back to the support user's own
      // account; the reload re-runs this check with the restored credentials.
      if (isImpersonating() && endImpersonation()) {
        setImpersonationNotice('expired')
        reloadAt('/dashboard/user-panel/profile')
        return
      }
      console.error('Auth verification failed. Clearing session.', error)
      removeAuthCookie('token')
      removeAuthCookie('role')
    }
  }

  await themePromise

  const store = configureStore(preloadedState)
  renderApp(store)
}

bootstrapApp()
